import { EyeOutlined } from '@ant-design/icons'
import { Tooltip, Typography } from 'antd'
import { Document } from 'features/files/types'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

import {
  getFileTypeIcon,
  isImageDocument,
  isPdfDocument,
} from './../utils/DocumentUtils'

const { Text } = Typography

const DocumentContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`

const DocumentInfo = styled.div`
  flex: 1;
  cursor: pointer;
`

const PreviewButton = styled.button`
  background: none;
  border: none;
  color: ${CantileverTheme.colors.grayscale[500]};
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  margin-left: 8px;
  font-size: 16px;

  &:hover {
    color: ${CantileverTheme.colors.SkyBlue};
    background: ${CantileverTheme.colors.blue[100]};
  }

  &:active {
    transform: scale(0.95);
  }
`

const DocumentItem = styled.div<{ selected?: boolean }>`
  padding: 10px 12px;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  background: ${({ selected, theme }) =>
    selected ? theme.forecast.cardGroupBackground : 'transparent'};
  border: 1px solid
    ${({ selected, theme }) =>
      selected ? theme.forecast.cardDividerBackground : 'transparent'};
  transition:
    background 0.2s,
    border 0.2s;
  &:hover {
    background: ${({ theme }) => theme.forecast.cardGroupBackground};
    border-color: ${({ theme }) => theme.forecast.cardDividerBackground};
  }
`

const DocumentHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
`

const MB_IN_BYTES = 1000000

const formatFileSize = (size?: number): string => {
  if (!size) return 'Size not available'
  return `${(size / MB_IN_BYTES).toFixed(2)} MB`
}

export interface DocumentListItemProps {
  document: Document
  selected: boolean
  onSelect: (documentId: string) => void
  onPreview?: (documentId: string) => void
}

export const DocumentListItem: React.FC<DocumentListItemProps> = ({
  document,
  selected,
  onSelect,
  onPreview,
}: DocumentListItemProps) => {
  const isPdf = isPdfDocument(document)
  const isImage = isImageDocument(document)
  const canPreview = isPdf || isImage

  return (
    <DocumentItem selected={selected}>
      <DocumentContent>
        <DocumentInfo onClick={() => onSelect(document.entryId)}>
          <DocumentHeader>
            {getFileTypeIcon(document)}
            <Text strong style={{ marginLeft: 8 }}>
              {document.name}
            </Text>
          </DocumentHeader>
          <Text type='secondary' style={{ fontSize: 12, marginLeft: 24 }}>
            {formatFileSize(document.size)}
          </Text>
        </DocumentInfo>
        {canPreview && onPreview && (
          <Tooltip title={`Preview ${isPdf ? 'PDF' : 'Image'}`} placement='top'>
            <PreviewButton
              onClick={(e) => {
                e.stopPropagation()
                onPreview(document.entryId)
              }}>
              <EyeOutlined />
            </PreviewButton>
          </Tooltip>
        )}
      </DocumentContent>
    </DocumentItem>
  )
}
